'use client'

import { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { SearchIcon, FilterIcon, XIcon, CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ProductsFilterParams } from '@/types/product'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CategoryCombobox } from '@/components/ui/category-combobox'
import { Combobox } from '@/components/ui/combobox'
import { useBrands, useModels, useCategoriesTree, useVehicleGroups } from '@/hooks'
import { CategoryType } from '@/types/category'
import { cn } from '@/lib/utils'

type PropsType = {
  filters: ProductsFilterParams
  onFiltersChange: (filters: Partial<ProductsFilterParams>) => void
  onResetFilters: () => void
}

const Filters = ({ filters, onFiltersChange, onResetFilters }: PropsType) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)

  const { register, handleSubmit, watch, setValue, reset } = useForm<ProductsFilterParams>({
    defaultValues: filters
  })

  useEffect(() => {
    reset(filters)
  }, [filters, reset])

  const watchAll = watch()

  // Fetch categories tree at once
  const { data: categoriesTree } = useCategoriesTree()

  // Helper function to find a category and its parents in the tree
  const findCategoryWithParents = useMemo(() => {
    if (!categoriesTree) {
      return () => []
    }

    const categoryMap = new Map<string, string[]>()

    const buildParentMap = (categories: CategoryType[], parentIds: string[] = []) => {
      for (const category of categories) {
        categoryMap.set(category.id, parentIds)
        if (category.children && category.children.length > 0) {
          buildParentMap(category.children, [...parentIds, category.id])
        }
      }
    }

    buildParentMap(categoriesTree)
    return (categoryId: string) => categoryMap.get(categoryId) || []
  }, [categoriesTree])

  // Build category IDs for brands filtering (selected categories + their parents)
  const categoryIdsForBrands = useMemo(() => {
    if (!watchAll.categoryIds || watchAll.categoryIds.length === 0) {
      return []
    }

    const allCategoryIds = new Set<string>()

    for (const categoryId of watchAll.categoryIds) {
      allCategoryIds.add(categoryId)
      const parentIds = findCategoryWithParents(categoryId)
      parentIds.forEach(id => allCategoryIds.add(id))
    }

    return Array.from(allCategoryIds)
  }, [watchAll.categoryIds, findCategoryWithParents])

  // Fetch vehicle groups
  const { data: vehicleGroupsData } = useVehicleGroups({
    size: 999999,
    categoryIds: categoryIdsForBrands.length > 0 ? categoryIdsForBrands : undefined
  })

  // Prepare vehicle group options
  const vehicleGroupOptions = useMemo(() => {
    if (!vehicleGroupsData?.items) {
      return []
    }
    return vehicleGroupsData.items.map(vehicleGroup => ({
      label: vehicleGroup.translations['en-US']?.title || vehicleGroup.id,
      value: vehicleGroup.id,
      searchValue: vehicleGroup.translations['en-US']?.title || vehicleGroup.id
    }))
  }, [vehicleGroupsData])

  // Fetch brands based on selected categories
  // If there are brands from URL but no categories selected yet, still fetch brands without a filter
  const hasBrandsFromUrl = filters.brandIds && filters.brandIds.length > 0
  const shouldFetchBrands = categoryIdsForBrands.length > 0 || hasBrandsFromUrl
  const { data: brandsData } = useBrands(
    {
      size: 999999,
      categoryIds: categoryIdsForBrands.length > 0 ? categoryIdsForBrands : undefined,
      vehicleGroupIds:
        watchAll.vehicleGroupIds && watchAll.vehicleGroupIds.length > 0 ? watchAll.vehicleGroupIds : undefined
    },
    { enabled: shouldFetchBrands }
  )

  // Prepare brand options
  const brandOptions = useMemo(() => {
    if (!brandsData?.items) {
      return []
    }
    return brandsData.items.map(brand => ({
      label: brand.name,
      value: brand.id,
      searchValue: brand.name
    }))
  }, [brandsData])

  // Get selected brands for model filtering
  const selectedBrandIds = watchAll.brandIds || []

  // Fetch models only when at least one brand is selected
  const { data: modelsData } = useModels(
    {
      size: 999999,
      brandIds: selectedBrandIds.length > 0 ? selectedBrandIds : undefined,
      categoryIds: categoryIdsForBrands.length > 0 ? categoryIdsForBrands : undefined,
      vehicleGroupIds:
        watchAll.vehicleGroupIds && watchAll.vehicleGroupIds.length > 0 ? watchAll.vehicleGroupIds : undefined
    },
    { enabled: selectedBrandIds.length > 0 }
  )

  // Prepare model options - only show if at least one brand is selected
  const modelOptions = useMemo(() => {
    if (selectedBrandIds.length === 0 || !modelsData?.items) {
      return []
    }
    return modelsData.items.map(model => ({
      label: model.name,
      value: model.id,
      searchValue: model.name
    }))
  }, [modelsData, selectedBrandIds.length])

  // Track if this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Mark the initial load as complete after the form is initialized with filters
  useEffect(() => {
    // Wait a moment for the form to be initialized with URL filters
    const timer = setTimeout(() => {
      setIsInitialLoad(false)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  // Clear brand and model when categories change and the brand becomes invalid (but not on initial load)
  useEffect(() => {
    if (isInitialLoad) {
      return
    }
    // If categories are selected but no brands available, clear brand and model
    if (categoryIdsForBrands.length > 0 && brandOptions.length === 0 && brandsData) {
      if (watchAll.brandIds || watchAll.modelIds) {
        setValue('brandIds', undefined)
        setValue('modelIds', undefined)
      }
      return
    }
    // If a brand is selected but no longer valid for current categories, clear both
    if (watchAll.brandIds && watchAll.brandIds.length > 0 && brandOptions.length > 0) {
      const brandStillValid = brandOptions.some(opt => watchAll.brandIds?.includes(opt.value))
      if (!brandStillValid) {
        setValue('brandIds', undefined)
        setValue('modelIds', undefined)
      }
    }
    // If a brand is cleared/removed, also clear models
    if (!watchAll.brandIds && watchAll.modelIds) {
      setValue('modelIds', undefined)
    }
  }, [
    brandOptions,
    brandsData,
    watchAll.brandIds,
    watchAll.modelIds,
    setValue,
    isInitialLoad,
    categoryIdsForBrands.length
  ])

  // Clear vehicle groups when categories change and vehicle groups become invalid (but not on initial load)
  useEffect(() => {
    if (isInitialLoad) {
      return
    }
    if (watchAll.vehicleGroupIds && watchAll.vehicleGroupIds.length > 0) {
      // Check if selected vehicle groups are still valid for current categories
      const validVehicleGroups = watchAll.vehicleGroupIds.filter(vgId =>
        vehicleGroupOptions.some(opt => opt.value === vgId)
      )
      if (validVehicleGroups.length !== watchAll.vehicleGroupIds.length) {
        setValue('vehicleGroupIds', validVehicleGroups.length > 0 ? validVehicleGroups : undefined)
      }
    }
  }, [vehicleGroupOptions, watchAll.vehicleGroupIds, setValue, isInitialLoad])

  // Clear models when the brand changes (but not on the initial load)
  useEffect(() => {
    if (isInitialLoad) {
      return
    }
    if (watchAll.modelIds && watchAll.modelIds.length > 0) {
      // Check if selected models are still valid for the current brand
      const validModels = watchAll.modelIds.filter(modelId => modelOptions.some(opt => opt.value === modelId))
      if (validModels.length !== watchAll.modelIds.length) {
        setValue('modelIds', validModels.length > 0 ? validModels : undefined)
      }
    }
  }, [modelOptions, watchAll.modelIds, setValue, isInitialLoad])

  const onSubmit = (data: ProductsFilterParams) => {
    onFiltersChange({ ...data, page: 1 })
    setIsOpen(false)
  }

  const handleClearSearch = () => {
    setValue('q', '')
  }

  const formatDateLabel = (value?: string) => (value ? format(new Date(value), 'dd.MM.yyyy') : t('start_date'))

  const handleCalendarChange = (field: keyof ProductsFilterParams, date?: Date) => {
    if (date) {
      setValue(field, date.toISOString())
    } else {
      setValue(field, undefined)
    }
  }

  const handleStatusChange = (value: string) => {
    if (value === 'all') {
      setValue('active', undefined)
    } else {
      setValue('active', value)
    }
  }

  const hasActiveFilters = () => {
    return (
      watchAll.q ||
      watchAll.createdAtStart ||
      watchAll.createdAtEnd ||
      watchAll.updatedAtStart ||
      watchAll.updatedAtEnd ||
      watchAll.active ||
      (watchAll.categoryIds &&
        typeof watchAll.categoryIds === 'object' &&
        'length' in watchAll.categoryIds &&
        watchAll.categoryIds.length > 0) ||
      (watchAll.vehicleGroupIds &&
        typeof watchAll.vehicleGroupIds === 'object' &&
        'length' in watchAll.vehicleGroupIds &&
        watchAll.vehicleGroupIds.length > 0) ||
      (watchAll.brandIds &&
        typeof watchAll.brandIds === 'object' &&
        'length' in watchAll.brandIds &&
        watchAll.brandIds.length > 0) ||
      (watchAll.modelIds &&
        typeof watchAll.modelIds === 'object' &&
        'length' in watchAll.modelIds &&
        watchAll.modelIds.length > 0) ||
      watchAll.priceMin !== undefined ||
      watchAll.priceMax !== undefined ||
      watchAll.stockMin !== undefined ||
      watchAll.stockMax !== undefined
    )
  }

  return (
    <div className="flex items-center justify-end">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="outline">
            <FilterIcon className="h-4 w-4" />
            <span className="hidden flex-row items-center justify-items-center lg:flex">{t('filters')}</span>
            {hasActiveFilters() && (
              <span className="bg-primary text-primary-foreground flex h-5 w-5 items-center justify-center rounded-full text-xs">
                !
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('filter_records')}</SheetTitle>
            <SheetDescription>{t('filter_description')}</SheetDescription>
          </SheetHeader>

          <ScrollArea>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-4">
              <div className="space-y-2">
                <Label htmlFor="filter-q">{t('keyword')}</Label>
                <div className="relative">
                  <Input
                    placeholder={t('keyword')}
                    id="filter-q"
                    {...register('q')}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSubmit(onSubmit)()
                      }
                    }}
                    icon={<SearchIcon className="h-4 w-4" />}
                    className="pr-8"
                  />
                  {watchAll.q && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1.5 right-1 h-6 w-6 p-0"
                      onClick={handleClearSearch}
                      type="button">
                      <XIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* CATEGORY FILTER */}
              <div className="space-y-2">
                <Label>{t('categories')}</Label>
                <CategoryCombobox
                  multiple
                  value={watchAll.categoryIds || []}
                  onValueChange={vals => {
                    if (Array.isArray(vals)) {
                      setValue('categoryIds', vals)
                    }
                  }}
                  clearable
                />
              </div>

              {/* VEHICLE GROUPS FILTER */}
              <div className="space-y-2">
                <Label>{t('vehicle_groups')}</Label>
                <Combobox
                  options={vehicleGroupOptions}
                  value={watchAll.vehicleGroupIds || []}
                  onValueChange={vals => {
                    if (Array.isArray(vals)) {
                      setValue('vehicleGroupIds', vals.length > 0 ? vals : undefined)
                    }
                  }}
                  placeholder={t('select')}
                  searchPlaceholder={t('search')}
                  emptyText={t('not_found')}
                  multiple
                  clearable
                />
              </div>

              {/* BRAND FILTER */}
              <div className="space-y-2">
                <Label>{t('brands')}</Label>
                <Combobox
                  options={brandOptions}
                  value={watchAll.brandIds || []}
                  onValueChange={vals => {
                    if (Array.isArray(vals)) {
                      setValue('brandIds', vals.length > 0 ? vals : undefined)
                      // Clear models when brands change
                      setValue('modelIds', undefined)
                    }
                  }}
                  placeholder={t('select')}
                  searchPlaceholder={t('search')}
                  emptyText={brandOptions.length === 0 ? t('not_found') : ''}
                  multiple
                  clearable
                />
              </div>

              {/* MODEL FILTER */}
              <div className="space-y-2">
                <Label>{t('models')}</Label>
                <Combobox
                  options={modelOptions}
                  value={watchAll.modelIds || []}
                  onValueChange={vals => {
                    if (Array.isArray(vals)) {
                      setValue('modelIds', vals.length > 0 ? vals : undefined)
                    }
                  }}
                  placeholder={t('select')}
                  searchPlaceholder={t('search')}
                  emptyText={modelOptions.length === 0 ? t('not_found') : ''}
                  disabled={selectedBrandIds.length === 0}
                  multiple
                  clearable
                />
              </div>

              {/* STATUS FILTER */}
              <div className="space-y-2">
                <Label>{t('status')}</Label>
                <Select value={watchAll.active ?? 'all'} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all')}</SelectItem>
                    <SelectItem value="true">{t('active')}</SelectItem>
                    <SelectItem value="false">{t('inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* PRICE RANGE FILTER */}
              <div className="space-y-2">
                <Label>{t('price_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <NumberInput
                    placeholder={t('min')}
                    min={0}
                    decimalScale={2}
                    value={watchAll.priceMin}
                    onValueChange={v => setValue('priceMin', v)}
                  />
                  <NumberInput
                    placeholder={t('max')}
                    min={0}
                    decimalScale={2}
                    value={watchAll.priceMax}
                    onValueChange={v => setValue('priceMax', v)}
                  />
                </div>
              </div>

              {/* STOCK RANGE FILTER */}
              <div className="space-y-2">
                <Label>{t('stock_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <NumberInput
                    placeholder={t('min')}
                    min={0}
                    decimalScale={0}
                    value={watchAll.stockMin}
                    onValueChange={v => setValue('stockMin', v)}
                  />
                  <NumberInput
                    placeholder={t('max')}
                    min={0}
                    decimalScale={0}
                    value={watchAll.stockMax}
                    onValueChange={v => setValue('stockMax', v)}
                  />
                </div>
              </div>

              {/** CREATED DATE RANGE */}
              <div className="space-y-2">
                <Label>{t('created_date_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(['createdAtStart', 'createdAtEnd'] as const).map(field => (
                    <Popover key={field}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'justify-start gap-1 text-left font-normal',
                            !watchAll[field] && 'text-muted-foreground'
                          )}>
                          <CalendarIcon className="mr-1 h-4 w-4" />
                          {watchAll[field]
                            ? formatDateLabel(watchAll[field])
                            : field === 'createdAtStart'
                              ? t('start_date')
                              : t('end_date')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          captionLayout="dropdown"
                          startMonth={new Date(1900, 0)}
                          endMonth={new Date()}
                          mode="single"
                          selected={watchAll[field] ? new Date(watchAll[field]) : undefined}
                          onSelect={date => handleCalendarChange(field, date || undefined)}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </div>
              </div>

              {/** UPDATED DATE RANGE */}
              <div className="space-y-2">
                <Label>{t('updated_date_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(['updatedAtStart', 'updatedAtEnd'] as const).map(field => (
                    <Popover key={field}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'justify-start gap-1 text-left font-normal',
                            !watchAll[field] && 'text-muted-foreground'
                          )}>
                          <CalendarIcon className="mr-1 h-4 w-4" />
                          {watchAll[field]
                            ? formatDateLabel(watchAll[field])
                            : field === 'updatedAtStart'
                              ? t('start_date')
                              : t('end_date')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          captionLayout="dropdown"
                          startMonth={new Date(1900, 0)}
                          endMonth={new Date()}
                          mode="single"
                          selected={watchAll[field] ? new Date(watchAll[field]) : undefined}
                          onSelect={date => handleCalendarChange(field, date || undefined)}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </div>
              </div>

              <div className="flex flex-row gap-2">
                <Button type="submit" variant="default" className="flex-1">
                  <FilterIcon className="h-4 w-4" />
                  {t('filter')}
                </Button>
                <Button
                  onClick={() => {
                    reset()
                    onResetFilters()
                  }}
                  variant="destructive"
                  className="flex-1"
                  type="button">
                  <XIcon className="h-4 w-4" />
                  {t('clear_filters')}
                </Button>
              </div>
            </form>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default Filters
